import React from 'react';
import {
  Button,
  Divider,
  IconButton,
  Input,
  Option,
  Select,
  Stack,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/joy';
import SearchIcon from '@mui/icons-material/Search';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseIcon from '@mui/icons-material/Close';

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'post-complete', label: 'Post-Complete' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'success', label: 'Success' },
  { value: 'error', label: 'Has Errors' },
  { value: 'never', label: 'Never Run' },
];

const FunctionsToolbar = ({
  // Search
  searchQuery,
  onSearchChange,
  // Filters
  typeFilter,
  onTypeFilterChange,
  statusFilter,
  onStatusFilterChange,
  // View toggle
  view,
  onViewChange,
  // Actions
  onRefresh,
  onCreateNew,
  // State
  loading,
}) => {
  const hasFilters = searchQuery || typeFilter || statusFilter;

  const handleClearFilters = () => {
    onSearchChange('');
    onTypeFilterChange('');
    onStatusFilterChange('');
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap"
      gap={2}
      sx={{
        py: 1.5,
        px: 0,
      }}
    >
      {/* Left side - Search */}
      <Stack direction="row" alignItems="center" gap={2} flex={1} minWidth={200}>
        <Input
          size="sm"
          placeholder="Search functions..."
          startDecorator={<SearchIcon sx={{ color: 'neutral.500' }} />}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          endDecorator={
            searchQuery && (
              <IconButton
                size="sm"
                variant="plain"
                onClick={() => onSearchChange('')}
                sx={{ mr: -0.5 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )
          }
          sx={{
            maxWidth: 250,
            flex: 1,
            '--Input-focusedThickness': '1px',
          }}
        />
      </Stack>

      {/* Center - Filters */}
      <Stack direction="row" alignItems="center" gap={1}>
        <Select
          size="sm"
          value={typeFilter}
          onChange={(_, value) => onTypeFilterChange(value || '')}
          sx={{ minWidth: 130 }}
          placeholder="All Types"
        >
          {TYPE_OPTIONS.map((option) => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>

        <Select
          size="sm"
          value={statusFilter}
          onChange={(_, value) => onStatusFilterChange(value || '')}
          sx={{ minWidth: 130 }}
          placeholder="All Status"
        >
          {STATUS_OPTIONS.map((option) => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>

        {hasFilters && (
          <Tooltip title="Clear filters">
            <IconButton
              size="sm"
              variant="plain"
              color="neutral"
              onClick={handleClearFilters}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {/* Right side - View Toggle and Actions */}
      <Stack direction="row" alignItems="center" gap={1}>
        <Tooltip title="Refresh">
          <IconButton
            size="sm"
            variant="plain"
            color="neutral"
            onClick={onRefresh}
            disabled={loading}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" sx={{ height: 24 }} />

        <ToggleButtonGroup
          size="sm"
          value={view}
          onChange={(_, newValue) => newValue && onViewChange(newValue)}
          sx={{
            '--ButtonGroup-separatorColor': 'transparent',
          }}
        >
          <Tooltip title="Grid view">
            <IconButton value="grid" variant={view === 'grid' ? 'solid' : 'outlined'}>
              <GridViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="List view">
            <IconButton value="list" variant={view === 'list' ? 'solid' : 'outlined'}>
              <ViewListIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </ToggleButtonGroup>

        <Divider orientation="vertical" sx={{ height: 24 }} />

        <Button
          size="sm"
          startDecorator={<AddRoundedIcon />}
          onClick={onCreateNew}
        >
          Create Function
        </Button>
      </Stack>
    </Stack>
  );
};

export default FunctionsToolbar;
